// src/components/BookingModal.jsx
// Booking flow: Calendar → Slot roll → Confirmation → Success

import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Clock, Users, Check, Loader } from "lucide-react";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

const MONTHS = ["January","February","March","April","May","June",
                 "July","August","September","October","November","December"];
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function Calendar({ year, month, availableDays, onDaySelect, selectedDay, onMonthChange }) {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startOffset = (firstDay.getDay() + 6) % 7; // Mon=0
  const totalDays = lastDay.getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const dateStr = (d) => `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const isPast = (d) => new Date(year, month - 1, d) <= today;
  const isAvailable = (d) => availableDays && availableDays[dateStr(d)];
  const isSelected = (d) => selectedDay === dateStr(d);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  while (weeks[weeks.length - 1]?.length < 7) weeks[weeks.length - 1].push(null);

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <button onClick={() => onMonthChange(-1)} style={{
          background:"#E8EDE0", border:"1px solid #2A2A2A", borderRadius:7,
          color:"#5A6E48", cursor:"pointer", padding:"6px 10px", display:"flex", alignItems:"center",
        }}><ChevronLeft size={14}/></button>
        <span style={{ fontSize:15, fontWeight:600, color:"#1E2C14" }}>
          {MONTHS[month-1]} {year}
        </span>
        <button onClick={() => onMonthChange(1)} style={{
          background:"#E8EDE0", border:"1px solid #2A2A2A", borderRadius:7,
          color:"#5A6E48", cursor:"pointer", padding:"6px 10px", display:"flex", alignItems:"center",
        }}><ChevronRight size={14}/></button>
      </div>

      {/* Day headers */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4, marginBottom:4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign:"center", fontSize:10, color:"#7A8E68", fontWeight:600, padding:"4px 0" }}>
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4, marginBottom:4 }}>
          {week.map((d, di) => {
            if (!d) return <div key={di}/>;
            const past = isPast(d);
            const avail = isAvailable(d);
            const sel = isSelected(d);
            const slots = avail ? availableDays[dateStr(d)] : [];

            return (
              <button key={di} onClick={() => avail && onDaySelect(dateStr(d))}
                disabled={past || !avail}
                style={{
                  aspectRatio:"1", borderRadius:8, border: sel ? "2px solid #3D4A1A" : "1px solid transparent",
                  background: sel ? "#C4D0A8" : (avail && !past) ? "rgba(61,74,26,0.1)" : "transparent",
                  color: past ? "#C4D0A8" : avail ? (sel ? "#2C3820" : "#3D4A1A") : "#C4D0A8",
                  cursor: avail ? "pointer" : "default",
                  fontSize:13, fontWeight: avail ? 600 : 400,
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2,
                  transition:"all 0.15s",
                  position:"relative",
                  opacity: past ? 0.4 : 1,
                  outline: "none",
                }}>
                {d}
                {avail && (
                  <div style={{ fontSize:8, color: sel ? "#886070" : "#6B2737" }}>
                    {past ? "" : `${slots.length} slot${slots.length !== 1 ? "s" : ""}`}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function SlotRoll({ slots, selectedSlot, onSelect, partySize }) {
  return (
    <div style={{ marginTop:20 }}>
      <div style={{ fontSize:11, fontWeight:600, color:"#7A8E68", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
        Available times
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:200, overflowY:"auto" }}>
        {slots.map((slot, i) => {
          const sel = selectedSlot?.slot_time === slot.slot_time;
          return (
            <button key={i} onClick={() => onSelect(slot)} style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"10px 14px", borderRadius:8,
              border: sel ? "1px solid #3A2028" : "1px solid #222",
              background: sel ? "#E2D8DA" : "#EDF1E4",
              cursor:"pointer", transition:"all 0.15s",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <Clock size={13} color={sel ? "#886070" : "#6A7E58"} />
                <span style={{ fontSize:13, fontWeight:600, color: sel ? "#C48890" : "#2C3820" }}>
                  {slot.slot_time}
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Users size={11} color="#6A7E58" />
                <span style={{ fontSize:11, color:"#5A6E48" }}>
                  {slot.remaining} left
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConfirmScreen({ winery, selectedDay, selectedSlot, partySize, onConfirm, onBack, loading }) {
  const { user } = useAuth("traveler");
  const [name, setName] = useState(user?.role === "traveler" ? (user.full_name || "") : "");
  const [email, setEmail] = useState(user?.role === "traveler" ? (user.email || "") : "");

  const inputStyle = {
    width:"100%", background:"#E8EDE0", border:"1px solid #222",
    borderRadius:8, padding:"10px 14px", fontSize:13,
    fontFamily:"'Inter',sans-serif", color:"#2C3820", outline:"none",
    boxSizing:"border-box",
  };

  return (
    <div>
      <div style={{ fontSize:11, fontWeight:600, color:"#7A8E68", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:16 }}>
        Confirm booking
      </div>

      {/* Summary */}
      <div style={{ background:"rgba(107,39,55,0.06)", border:"1px solid rgba(107,39,55,0.15)", borderRadius:10, padding:"14px 16px", marginBottom:20 }}>
        <div style={{ fontSize:15, fontWeight:600, color:"#1E2C14", marginBottom:8 }}>{winery.name}</div>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <div style={{ fontSize:12, color:"#5A6E48" }}>📅 {selectedDay}</div>
          <div style={{ fontSize:12, color:"#5A6E48" }}>⏰ {selectedSlot.slot_time}</div>
          <div style={{ fontSize:12, color:"#5A6E48" }}>👥 {partySize} {partySize === 1 ? "person" : "people"}</div>
        </div>
      </div>

      {/* Contact */}
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
        <input style={inputStyle} placeholder="Your name" value={name}
          onChange={e => setName(e.target.value)} />
        <input style={inputStyle} placeholder="Email address" type="email" value={email}
          onChange={e => setEmail(e.target.value)} />
      </div>

      <div style={{ display:"flex", gap:8 }}>
        <button onClick={onBack} style={{
          flex:1, padding:"11px", background:"#E8EDE0", border:"1px solid #2A2A2A",
          borderRadius:8, color:"#5A6E48", fontSize:13, cursor:"pointer",
        }}>Back</button>
        <button onClick={() => onConfirm(name, email)}
          disabled={!name || !email || loading}
          style={{
            flex:2, padding:"11px", background: name && email && !loading ? "#6B2737" : "#EAEFE2",
            border:"none", borderRadius:8,
            color: name && email && !loading ? "#DCE4EC" : "#7A8E68",
            fontSize:13, fontWeight:500, cursor: name && email && !loading ? "pointer" : "not-allowed",
            display:"flex", alignItems:"center", justifyContent:"center", gap:6,
          }}>
          {loading ? <><Loader size={13} style={{animation:"spin 1s linear infinite"}} /> Processing…</> : "Confirm booking"}
        </button>
      </div>
    </div>
  );
}

function SuccessScreen({ booking, onClose }) {
  return (
    <div style={{ textAlign:"center", padding:"20px 0" }}>
      <div style={{
        width:56, height:56, borderRadius:"50%",
        background:"rgba(74,112,82,0.15)", border:"1px solid rgba(74,112,82,0.3)",
        display:"flex", alignItems:"center", justifyContent:"center",
        margin:"0 auto 16px",
      }}>
        <Check size={24} color="#6AAA7A" />
      </div>
      <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:"#1E2C14", margin:"0 0 8px" }}>
        Booking request sent!
      </h3>
      <p style={{ fontSize:13, color:"#5A6E48", margin:"0 0 20px", lineHeight:1.5 }}>
        Your tasting at <strong style={{color:"#2C3820"}}>{booking.winery_name || "the winery"}</strong> on{" "}
        <strong style={{color:"#2C3820"}}>{booking.visit_date}</strong> at{" "}
        <strong style={{color:"#2C3820"}}>{booking.slot_time}</strong> is pending approval. The winery will confirm your booking by email.
      </p>
      <p style={{ fontSize:11, color:"#6A7E58", margin:"0 0 20px" }}>
        Confirmation sent to {booking.user_email}
      </p>
      <button onClick={onClose} style={{
        padding:"11px 24px", background:"#6B2737", border:"none",
        borderRadius:8, color:"#DCE4EC", fontSize:13, fontWeight:500, cursor:"pointer",
      }}>Done</button>
    </div>
  );
}

export default function BookingModal({ winery, partySize = 2, onClose, planId, legIndex, departureAt, tripRef, onBooked }) {
  // Trip-aware mode: this winery was resolved to a specific leg of a
  // generated plan (see TourResultsPage.jsx's openBooking), so slot
  // eligibility runs through the exact same live feasibility calculation
  // "Book this tour" uses (occasion_type/party_size/earliest-arrival/
  // window) — see api.getBookingAvailability and mcp-booking's
  // get_plan_stop_slots. Falls back to plain, trip-unaware month browsing
  // when no plan context resolved (e.g. winery isn't part of this plan).
  const tripAware = planId != null && legIndex != null && !!departureAt;

  const today = new Date();
  const initial = tripAware ? new Date(departureAt) : today;
  const [year, setYear] = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth() + 1);
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState("calendar"); // calendar | confirm | success
  const [booking, setBooking] = useState(null);
  const [loadingBook, setLoadingBook] = useState(false);
  const [error, setError] = useState("");
  const [noSlotsMessage, setNoSlotsMessage] = useState("");

  // Use winery_id from DB if available, otherwise use a default for prototype
  const wineryId = winery?.winery_id || winery?.id || "33333333-3333-3333-3333-333333333305";

  const loadAvailability = useCallback(async () => {
    setLoadingAvail(true); setNoSlotsMessage("");
    try {
      if (tripAware) {
        const res = await api.getBookingAvailability(wineryId, { partySize, planId, legIndex, departureAt, tripRef });
        if (res.feasible) {
          // Flat candidates -> the same {date: [slots]} shape Calendar/
          // SlotRoll already render, so the rest of this component's UI is
          // unchanged regardless of which mode fetched the data.
          const grouped = {};
          for (const c of res.candidates || []) {
            (grouped[c.date] ||= []).push({ slot_time: c.slot_time, remaining: c.remaining, capacity: c.capacity });
          }
          setAvailability({ available_days: grouped });
        } else {
          setAvailability({ available_days: {} });
          setNoSlotsMessage(res.message || "No slots available for the booking");
        }
      } else {
        const res = await api.getBookingAvailability(wineryId, { year, month, partySize });
        setAvailability(res);
      }
    } catch (e) {
      setError("Failed to load availability.");
    } finally {
      setLoadingAvail(false);
    }
    // year/month are in this callback's deps even in trip-aware mode, where
    // they're unused — that just makes month navigation re-issue an
    // idempotent request (same candidates come back) instead of needing a
    // second code path to keep in sync. Simpler and still always correct;
    // the plan window is small (plans cap at 7 days) so the extra round
    // trip is cheap.
  }, [wineryId, partySize, tripAware, planId, legIndex, departureAt, tripRef, year, month]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const handleMonthChange = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
    setSelectedDay(null); setSlots([]); setSelectedSlot(null);
  };

  const handleDaySelect = (dateStr) => {
    setSelectedDay(dateStr);
    setSelectedSlot(null);
    const daySlots = availability?.available_days?.[dateStr] || [];
    setSlots(daySlots);
  };

  const handleConfirm = async (name, email) => {
    setLoadingBook(true); setError("");
    try {
      const res = await api.createBooking({
        winery_id: wineryId || "00000000-0000-0000-0000-000000000001",
        visit_date: selectedDay,
        slot_time: selectedSlot.slot_time,
        party_size: partySize,
        user_name: name,
        user_email: email,
        // Carried through when this booking came from a generated plan, so
        // the live engine's dwell-time sum for other stops (booked via
        // either this flow or the "Book this tour" stepper) sees it.
        ...(tripAware ? { plan_id: planId, trip_ref: tripRef } : {}),
      });
      setBooking({...res, winery_name: winery?.name, user_email: email});
      setStep("success");
      // Downstream stops' dwell-time sums (and this stop's own "already
      // booked" status) may have changed — let the results page
      // recalculate them. Only meaningful in trip-aware mode.
      if (tripAware && onBooked) onBooked(legIndex);
    } catch (e) {
      setError(e.message || "Booking failed. Please try again.");
    } finally {
      setLoadingBook(false);
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1100,
      background:"rgba(0,0,0,0.85)", backdropFilter:"blur(4px)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"#EDF1E4", border:"1px solid #252525", borderRadius:14,
        padding:24, maxWidth:420, width:"100%", maxHeight:"90vh", overflowY:"auto",
      }}>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, color:"#1E2C14" }}>
              {step === "success" ? "Request sent" : `Book tasting — ${winery?.name}`}
            </div>
            {step === "calendar" && (
              <div style={{ fontSize:11, color:"#6A7E58", marginTop:2 }}>
                {partySize} {partySize === 1 ? "person" : "people"}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background:"none", border:"none", color:"#5A6E48", cursor:"pointer", fontSize:18,
          }}>✕</button>
        </div>

        {error && (
          <div style={{ background:"rgba(107,39,55,0.1)", border:"1px solid rgba(107,39,55,0.2)", borderRadius:7, padding:"8px 12px", marginBottom:16, fontSize:12, color:"#886070" }}>
            {error}
          </div>
        )}

        {/* Step: Calendar */}
        {step === "calendar" && (
          <>
            {loadingAvail ? (
              <div style={{ textAlign:"center", padding:"40px 0", color:"#6A7E58", fontSize:13 }}>
                Loading availability…
              </div>
            ) : noSlotsMessage && Object.keys(availability?.available_days || {}).length === 0 ? (
              <div style={{ textAlign:"center", padding:"32px 0", color:"#886070", fontSize:13, fontWeight:500 }}>
                {noSlotsMessage}
              </div>
            ) : (
              <>
                <Calendar
                  year={year} month={month}
                  availableDays={availability?.available_days || {}}
                  onDaySelect={handleDaySelect}
                  selectedDay={selectedDay}
                  onMonthChange={handleMonthChange}
                />
                {selectedDay && slots.length > 0 && (
                  <>
                    <SlotRoll
                      slots={slots}
                      selectedSlot={selectedSlot}
                      onSelect={setSelectedSlot}
                      partySize={partySize}
                    />
                    {selectedSlot && (
                      <button onClick={() => setStep("confirm")} style={{
                        width:"100%", marginTop:16, padding:"11px",
                        background:"#6B2737", border:"none", borderRadius:8,
                        color:"#DCE4EC", fontSize:13, fontWeight:500, cursor:"pointer",
                      }}>
                        Continue to confirmation →
                      </button>
                    )}
                  </>
                )}
                {selectedDay && slots.length === 0 && (
                  <div style={{ textAlign:"center", padding:"20px 0", color:"#6A7E58", fontSize:13 }}>
                    No available slots for this day.
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Step: Confirm */}
        {step === "confirm" && (
          <ConfirmScreen
            winery={winery}
            selectedDay={selectedDay}
            selectedSlot={selectedSlot}
            partySize={partySize}
            onConfirm={handleConfirm}
            onBack={() => setStep("calendar")}
            loading={loadingBook}
          />
        )}

        {/* Step: Success */}
        {step === "success" && booking && (
          <SuccessScreen booking={booking} onClose={onClose} />
        )}
      </div>
    </div>
  );
}
